import { AutocompleteValue } from '@mui/base/useAutocomplete/useAutocomplete';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import {
  AutocompleteRenderOptionState,
  Box,
  Checkbox,
  Divider,
} from '@mui/material';
import { flatten } from 'lodash';
import React, {
  forwardRef,
  HTMLAttributes,
  PropsWithChildren,
  ReactElement,
  useEffect,
  useMemo,
  useState,
  FocusEvent,
} from 'react';
import { IPlainObject } from '../../models';
import { renderFilterTag } from './components/renderFilterTag';
import { SGAutocomplete, SGAutocompleteProps } from './SGAutocomplete';

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

export type SGAutocompleteValue<T> = AutocompleteValue<T, true, false, false>;

export interface SGRocketAutocompleteProps<T>
  extends Omit<SGAutocompleteProps<T, true>, 'onChange' | 'value'> {
  selectAllLabel?: string;
  value?: SGAutocompleteValue<T>;
  onChange?: (value: SGAutocompleteValue<T>, name?: string) => void;
  shortcutOptions?: (T & { values: T[] })[];
  getShortcutOptionLabel?: (option: T) => string;
  getTagLabel?: (option: T) => string;
}

function isValuesContainShortcutOption<T>(
  value: T[],
  shortcutValues: T[],
  isOptionEqualToValue: (o: T, v: T) => boolean,
): boolean {
  const scValues = shortcutValues.filter(
    (scValue) => !value.find((v) => isOptionEqualToValue(v, scValue)),
  );

  return scValues.length === 0;
}

export function SGRocketAutocomplete<T>({
  label,
  placeholder,
  name,
  loading,
  disabled,
  value: currentValue,
  onChange,
  onBlur,
  options,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  selectAllLabel,
  error,
  shortcutOptions,
  limitTags,
  getShortcutOptionLabel: _getShortcutOptionLabel,
  getTagLabel: _getTagLabel,
  ...props
}: SGRocketAutocompleteProps<T>): ReactElement {
  const [open, setIsOpen] = useState(false);
  const [value, setValue] = useState<SGAutocompleteValue<T> | undefined>(
    currentValue,
  );

  const getOptionLabel =
    props.getOptionLabel || ((option: T) => String(option));
  const getTagLabel = _getTagLabel || getOptionLabel;
  const getShortcutOptionLabel = _getShortcutOptionLabel || getOptionLabel;

  const { isOptionEqualToValue } = props;

  useEffect(() => {
    if (currentValue === value) {
      return;
    }

    setValue(currentValue);
  }, [currentValue]);

  useEffect(() => {
    if (open && (loading || disabled)) {
      setIsOpen(false);
    }
  }, [loading, disabled]);

  const close = (newValue?: T[]) => {
    setIsOpen(false);

    const val = newValue || value;

    if (!value || !onChange || currentValue === val) {
      return;
    }

    setValue(val);
    onChange(val as T[], name);
  };

  const selectedShortcutOption: (T & { values: T[] })[] = useMemo(() => {
    if (!shortcutOptions || !shortcutOptions.length || !isOptionEqualToValue) {
      return [];
    }

    return shortcutOptions.filter((scOption) =>
      isValuesContainShortcutOption(
        value || [],
        scOption.values,
        isOptionEqualToValue,
      ),
    );
  }, [value, shortcutOptions]);

  const onValueChange = (e: React.ChangeEvent<IPlainObject>, newValue: T[]) => {
    const clearValue = newValue.filter((v) => v) as T[];

    if ((e.target as IPlainObject).localName === 'input') {
      setValue(clearValue);
      return;
    }

    close(clearValue);
  };

  const handleOnBlur = (e: FocusEvent<HTMLDivElement>) => {
    close();

    onBlur && onBlur(e);
  };

  const onShortcutOptionClick = (
    e: React.ChangeEvent<IPlainObject>,
    { values: scOptionValues, ...otherOptionProps }: T & { values: T[] },
    addValues: boolean,
  ) => {
    const newValues = addValues
      ? [...(value || []), ...scOptionValues]
      : [
          ...(value || []).filter(
            (v) =>
              !scOptionValues.find((scOpt) => isOptionEqualToValue(scOpt, v)),
          ),
          ...flatten(
            selectedShortcutOption
              .map((selectedScOpt) =>
                props.isOptionEqualToValue(
                  selectedScOpt,
                  otherOptionProps as unknown as T,
                )
                  ? null
                  : selectedScOpt.values,
              )
              .filter((v) => v),
          ),
        ];
    const clearValue = options.filter((opt) =>
      newValues.find((newVal) => props.isOptionEqualToValue(opt, newVal)),
    );

    if ((e.target as IPlainObject).localName === 'input') {
      setValue(clearValue);
      return;
    }

    close(clearValue);
  };

  const renderOptionCb = (
    props: React.HTMLAttributes<HTMLLIElement>,
    option: T,
    { selected }: AutocompleteRenderOptionState,
  ): React.ReactNode => {
    return (
      <Box
        component="li"
        sx={{ '& > img': { mr: 2, flexShrink: 0 } }}
        {...props}
      >
        <Checkbox
          icon={icon}
          checkedIcon={checkedIcon}
          style={{ marginRight: 8 }}
          checked={selected}
        />
        <Box>{getOptionLabel(option)}</Box>
      </Box>
    );
  };

  const renderShortcutOption = (scOption: T & { values: T[] }) => {
    const isChecked = !!selectedShortcutOption.find((sc) =>
      props.isOptionEqualToValue(sc, scOption),
    );

    return (
      <li
        key={getShortcutOptionLabel(scOption)}
        role="option"
        aria-disabled="false"
        aria-selected={isChecked}
        className="MuiAutocomplete-option"
        onClick={(e) => onShortcutOptionClick(e, scOption, !isChecked)}
      >
        <Checkbox
          icon={icon}
          checkedIcon={checkedIcon}
          style={{ marginRight: 8 }}
          checked={isChecked}
        />
        <Box>{getShortcutOptionLabel(scOption)}</Box>
      </li>
    );
  };

  const additionProps = {
    ...(!value ? {} : { value }),
  };

  const isOpen = open && !loading && !disabled;

  const ListboxComponent = forwardRef<HTMLDivElement>(function ListboxComponent(
    props: PropsWithChildren<HTMLAttributes<HTMLElement>>,
    ref,
  ) {
    const { children, ...other } = props;

    return (
      <div ref={ref}>
        <ul {...other}>
          {shortcutOptions.map((scOption) => renderShortcutOption(scOption))}

          <Divider />

          {children}
        </ul>
      </div>
    );
  });

  return (
    <SGAutocomplete<T, true, false, false>
      multiple
      disabled={loading || disabled}
      onFocus={() => setIsOpen(true)}
      onOpen={() => setIsOpen(true)}
      onBlur={handleOnBlur}
      disableCloseOnSelect
      open={isOpen}
      renderOption={renderOptionCb}
      shrink
      size="small"
      name={name}
      label={label}
      placeholder={value && value.length ? undefined : placeholder}
      error={error}
      options={[...options]}
      renderTags={(tagValues, getTProps) =>
        renderFilterTag(
          tagValues,
          selectedShortcutOption,
          getTProps,
          getTagLabel,
          getShortcutOptionLabel,
          limitTags,
        )
      }
      ListboxComponent={
        shortcutOptions && !!shortcutOptions.length
          ? (ListboxComponent as React.ComponentType<
              React.HTMLAttributes<HTMLElement>
            >)
          : undefined
      }
      {...additionProps}
      {...props}
      getOptionLabel={getOptionLabel}
      onChange={onValueChange}
    />
  );

  // return <div>Autocomplete</div>;
}
